import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { GameState } from '../systems/GameState';
import { OutfitRenderer } from '../systems/OutfitRenderer';
import { svgStringToTexture } from '../utils/svgToTexture';

export class CharacterCreateScene extends Phaser.Scene {
  private charSprite: Phaser.GameObjects.Image | null = null;
  private nameDisplayText: Phaser.GameObjects.Text | null = null;

  constructor() { super('CharacterCreateScene'); }

  async create(): Promise<void> {
    this.cameras.main.setBackgroundColor('#fef0f5');
    if (this.textures.exists('bg_home')) {
      const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_home');
      // 화면을 채우도록 스케일 — cover 방식
      const sx = GAME_WIDTH / bg.width;
      const sy = GAME_HEIGHT / bg.height;
      const s = Math.max(sx, sy);
      bg.setScale(s).setDepth(-100);
      // 가독성용 살짝 톤 다운
      this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xfef0f5, 0.55).setDepth(-99);
    }
    this.cameras.main.fadeIn(300);

    // Decorative background
    for (let i = 0; i < 12; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      this.add.text(x, y, '✦', { fontSize: '18px', color: '#ffb0cc', alpha: 0.2 } as Phaser.Types.GameObjects.Text.TextStyle)
        .setOrigin(0.5).setAlpha(0.15 + Math.random() * 0.12);
    }

    this.add.text(GAME_WIDTH / 2, 80, "로하의 옷장에 오신 걸 환영해요!", {
      fontFamily: 'Pretendard Variable, sans-serif',
      fontSize: '34px',
      fontStyle: '800',
      color: '#3a2540',
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 140, "이름을 알려주세요 ✨", {
      fontFamily: 'Pretendard Variable, sans-serif',
      fontSize: '20px',
      color: '#7a5b80',
    }).setOrigin(0.5);

    // Show default character preview on the right side
    await this._renderDefaultChar();

    // Name display above character
    this.nameDisplayText = this.add.text(GAME_WIDTH * 0.72, GAME_HEIGHT / 2 - 180, '로하', {
      fontFamily: 'Pretendard Variable, sans-serif',
      fontSize: '22px',
      fontStyle: '800',
      color: '#ff6fa1',
      backgroundColor: '#fff',
      padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setDepth(10);

    // Native HTML form (input + submit) — Phaser button과 DOM input 충돌 회피
    const form = document.createElement('form');
    form.style.display = 'flex';
    form.style.flexDirection = 'column';
    form.style.gap = '24px';
    form.style.alignItems = 'center';

    const inputEl = document.createElement('input');
    inputEl.type = 'text';
    inputEl.value = '로하';
    inputEl.maxLength = 8;
    inputEl.style.cssText = `
      font-family: 'Pretendard Variable', Pretendard, sans-serif;
      font-size: 28px; font-weight: 700;
      padding: 18px 24px;
      border: 4px solid #ff6fa1; border-radius: 16px;
      text-align: center; color: #3a2540;
      width: 360px; outline: none;
      background: #fff;
    `;

    // Live name preview update
    inputEl.addEventListener('input', () => {
      const name = inputEl.value || '로하';
      if (this.nameDisplayText) this.nameDisplayText.setText(name);
    });

    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.textContent = '✨ 시작하기';
    submitBtn.style.cssText = `
      font-family: 'Pretendard Variable', Pretendard, sans-serif;
      font-size: 22px; font-weight: 800;
      padding: 18px 48px;
      background: linear-gradient(135deg, #ff6fa1, #e54e87);
      color: #fff;
      border: none; border-radius: 16px;
      cursor: pointer;
      box-shadow: 0 6px 16px rgba(255, 111, 161, 0.4);
      transition: transform 0.1s;
    `;
    submitBtn.onmouseover = () => { submitBtn.style.transform = 'translateY(-2px)'; };
    submitBtn.onmouseout = () => { submitBtn.style.transform = 'translateY(0)'; };

    form.appendChild(inputEl);
    form.appendChild(submitBtn);

    form.onsubmit = (e) => {
      e.preventDefault();
      const name = inputEl.value.trim() || '로하';
      GameState.initialize(name);
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start('HomeScene'));
    };

    // Position form on the LEFT half
    this.add.dom(GAME_WIDTH * 0.28, GAME_HEIGHT / 2, form);

    // 자동 포커스
    setTimeout(() => inputEl.focus(), 100);
  }

  private async _renderDefaultChar(): Promise<void> {
    try {
      if (this.charSprite) this.charSprite.destroy();
      if (this.textures.exists('character_bald')) {
        this.charSprite = this.add.image(GAME_WIDTH * 0.72, GAME_HEIGHT / 2 + 20, 'character_bald');
        this.charSprite.setScale(1.25);
      } else {
        const svg = OutfitRenderer.renderCharacterSVG({});
        const key = 'create_char_default';
        await svgStringToTexture(this, key, svg);
        this.charSprite = this.add.image(GAME_WIDTH * 0.72, GAME_HEIGHT / 2 + 20, key);
        this.charSprite.setScale(2.5);
      }
      // Gentle idle bob
      this.tweens.add({
        targets: this.charSprite,
        y: this.charSprite.y - 6,
        duration: 1400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.InOut',
      });
    } catch {
      // Ignore texture error
    }
  }
}
